/**
 * YG1 Product Tabs (desktop only)
 * - Stable ARIA tab behavior
 * - Clicking the currently-selected MAIN tab toggles the panel closed
 * - Interactive Product Page: fetched on demand from
 *   /products/<handle>?view=yg1-catalog-json  (all pages in one request)
 * - Interactive search covers ALL pages; optional exact-match column filters
 * - ISO Chart / Speeds & Feeds: rendered from inline JSON in yg1-cc-single.liquid
 * - Resolves SKU links via exact-match Shopify search JSON endpoint
 */

(function () {
  var DESKTOP_MIN = 990;

  function isDesktop() {
    return window.matchMedia && window.matchMedia("(min-width: " + DESKTOP_MIN + "px)").matches;
  }

  function normText(s) {
    return (s || "")
      .toLowerCase()
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function safeEscape(sel) {
    try {
      return CSS.escape(sel);
    } catch (e) {
      return sel.replace(/([ #;?%&,.+*~\':"!^$[\]()=>|\/@])/g, "\\$1");
    }
  }

  function findTabsRoots() {
    return Array.from(document.querySelectorAll(".yg1-tabs"));
  }

  function collapseTabs(root) {
    if (!root) return;

    var tabs = Array.from(root.querySelectorAll('.yg1-tabs__tab[role="tab"]'));
    var panels = Array.from(root.querySelectorAll('.yg1-tabs__panel[role="tabpanel"]'));

    tabs.forEach(function (t) {
      t.setAttribute("aria-selected", "false");
      t.tabIndex = -1;
    });

    panels.forEach(function (p) {
      p.hidden = true;
      p.style.display = "none";
    });

    root.setAttribute("data-yg1-tabs-collapsed", "true");
  }

  function activateTab(root, tabEl) {
    if (!root || !tabEl) return;

    var tabs = Array.from(root.querySelectorAll('.yg1-tabs__tab[role="tab"]'));
    var panels = Array.from(root.querySelectorAll('.yg1-tabs__panel[role="tabpanel"]'));

    tabs.forEach(function (t) {
      t.setAttribute("aria-selected", t === tabEl ? "true" : "false");
      t.tabIndex = t === tabEl ? 0 : -1;
    });

    var panelId = tabEl.getAttribute("aria-controls");
    var activePanel = null;

    panels.forEach(function (p) {
      var show = p.id && panelId && p.id === panelId;
      p.hidden = !show;
      p.style.display = show ? "" : "none";
      if (show) activePanel = p;
    });

    root.setAttribute("data-yg1-tabs-collapsed", "false");

    if (activePanel) {
      initInteractiveCatalogFromJson(activePanel);
      initCcSinglePanels(activePanel);
      enhanceCuttingConditionsTables(activePanel);
    }
  }

  function wireTabs(root) {
    var tablist = root.querySelector(".yg1-tabs__list");
    if (!tablist) return;

    tablist.setAttribute("role", "tablist");

    var tabs = Array.from(root.querySelectorAll(".yg1-tabs__tab"));
    tabs.forEach(function (t) {
      t.setAttribute("role", "tab");
      if (!t.hasAttribute("aria-selected")) t.setAttribute("aria-selected", "false");
      if (!t.hasAttribute("tabindex")) t.tabIndex = -1;
    });

    var panels = Array.from(root.querySelectorAll(".yg1-tabs__panel"));
    panels.forEach(function (p) {
      p.setAttribute("role", "tabpanel");
      if (typeof p.hidden === "undefined") p.style.display = "none";
      if (!p.id) p.id = "yg1-panel-" + Math.random().toString(36).slice(2);
    });

    tabs.forEach(function (t, idx) {
      if (!t.getAttribute("aria-controls")) {
        var p = panels[idx];
        if (p) t.setAttribute("aria-controls", p.id);
      }
    });

    tablist.addEventListener("click", function (e) {
      var btn = e.target.closest('.yg1-tabs__tab[role="tab"]');
      if (!btn || !root.contains(btn)) return;

      var isSelected = btn.getAttribute("aria-selected") === "true";
      var isCollapsed = root.getAttribute("data-yg1-tabs-collapsed") === "true";

      if (isSelected && !isCollapsed) {
        collapseTabs(root);
        return;
      }

      activateTab(root, btn);
    });

    tablist.addEventListener("keydown", function (e) {
      var current =
        document.activeElement &&
        document.activeElement.closest('.yg1-tabs__tab[role="tab"]');

      if (!current || !root.contains(current)) return;

      var tabsArr = Array.from(root.querySelectorAll('.yg1-tabs__tab[role="tab"]'));
      var idx = tabsArr.indexOf(current);
      if (idx < 0) return;

      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        var dir = e.key === "ArrowRight" ? 1 : -1;
        var next = tabsArr[(idx + dir + tabsArr.length) % tabsArr.length];
        next.focus();
        activateTab(root, next);
      }

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        var isSelected = current.getAttribute("aria-selected") === "true";
        var isCollapsed = root.getAttribute("data-yg1-tabs-collapsed") === "true";
        if (isSelected && !isCollapsed) {
          collapseTabs(root);
        } else {
          activateTab(root, current);
        }
      }
    });

    var preset = tabs.find(function (t) {
      return t.getAttribute("aria-selected") === "true";
    });

    activateTab(root, preset || tabs[0]);
  }

  // ---------------- JSON helpers ----------------

  function getJsonFromScriptId(scriptId) {
    if (!scriptId) return null;
    var el = document.getElementById(scriptId);
    if (!el) return null;
    var txt = (el.textContent || "").trim();
    if (!txt) return null;
    try {
      return JSON.parse(txt);
    } catch (e) {
      return { __parse_error: true, __error: e, __raw: txt };
    }
  }

  function normalizeRows(rows) {
    return Array.isArray(rows) ? rows : [];
  }

  function normalizeColumns(cols) {
    return Array.isArray(cols) ? cols : [];
  }

  // ---- exact SKU -> URL resolver ----

  var SKU_RESOLVE_CACHE = Object.create(null);
  var SKU_RESOLVE_INFLIGHT = Object.create(null);
  var SKU_CONCURRENCY = 6;
  var SKU_QUEUE = [];
  var SKU_ACTIVE = 0;

  function normalizeSkuForCompare(s) {
    s = (s == null) ? "" : String(s);
    s = s.trim();
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
      s = s.slice(1, -1).trim();
    }
    return s;
  }

  function buildSkuSearchUrl(sku) {
    return (
      "/search" +
      "?q=" + encodeURIComponent(sku) +
      "&type=product" +
      "&options[prefix]=none" +
      "&view=yg1-sku" +
      "&yg1_exact=1"
    );
  }

  function resolveSkuUrlExact(sku, cb) {
    sku = normalizeSkuForCompare(sku);
    if (!sku) return cb(null);

    if (Object.prototype.hasOwnProperty.call(SKU_RESOLVE_CACHE, sku)) {
      return cb(SKU_RESOLVE_CACHE[sku]);
    }

    if (SKU_RESOLVE_INFLIGHT[sku]) {
      SKU_RESOLVE_INFLIGHT[sku].push(cb);
      return;
    }
    SKU_RESOLVE_INFLIGHT[sku] = [cb];

    SKU_QUEUE.push(sku);
    drainSkuQueue();
  }

  function drainSkuQueue() {
    while (SKU_ACTIVE < SKU_CONCURRENCY && SKU_QUEUE.length) {
      var sku = SKU_QUEUE.shift();
      SKU_ACTIVE++;

      fetch(buildSkuSearchUrl(sku), { credentials: "same-origin" })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var wanted = normalizeSkuForCompare(sku);
          var url = null;

          if (data && Array.isArray(data.results)) {
            var hit = data.results.find(function (it) {
              return normalizeSkuForCompare(it && it.title) === wanted;
            });

            if (hit && hit.url) {
              url = String(hit.url);
              if (url.indexOf("http") === 0) {
                try {
                  var u = new URL(url);
                  url = u.pathname + u.search;
                } catch (e) {}
              }
              if (url.charAt(0) !== "/") url = "/" + url;
            } else if (hit && hit.handle) {
              url = "/products/" + String(hit.handle).trim();
            }
          }

          SKU_RESOLVE_CACHE[sku] = url;
          flushSkuCallbacks(sku, url);
        })
        .catch(function () {
          SKU_RESOLVE_CACHE[sku] = null;
          flushSkuCallbacks(sku, null);
        })
        .finally(function () {
          SKU_ACTIVE--;
          drainSkuQueue();
        });
    }
  }

  function flushSkuCallbacks(sku, url) {
    var cbs = SKU_RESOLVE_INFLIGHT[sku] || [];
    delete SKU_RESOLVE_INFLIGHT[sku];
    cbs.forEach(function (fn) {
      try { fn(url); } catch (e) {}
    });
  }

  function maybeLinkSkuCell(td, skuText) {
    var sku = normalizeSkuForCompare(skuText);
    if (!sku) return;

    td.textContent = sku;

    resolveSkuUrlExact(sku, function (url) {
      if (!url) return;
      if (normalizeSkuForCompare(td.textContent) !== sku) return;

      var a = document.createElement("a");
      a.href = url;
      a.textContent = sku;
      a.className = "yg1-interactive-link";
      td.textContent = "";
      td.appendChild(a);
    });
  }

  // ---------------- CC schema helpers ----------------

  var CC_DIAMETER_COLS = [
    { key: "d_3_32", label: "3/32" },
    { key: "d_1_8", label: "1/8" },
    { key: "d_5_32", label: "5/32" },
    { key: "d_3_16", label: "3/16" },
    { key: "d_1_4", label: "1/4" },
    { key: "d_5_16", label: "5/16" },
    { key: "d_3_8", label: "3/8" },
    { key: "d_1_2", label: "1/2" },
    { key: "d_9_16", label: "9/16" },
    { key: "d_5_8", label: "5/8" },
    { key: "d_13_16", label: "13/16" }
  ];

  function buildCcColumns() {
    return [
      { key: "iso", label: "ISO" },
      { key: "vdi", label: "VDI" },
      { key: "material", label: "Material" },
      { key: "ae", label: "Ae" },
      { key: "ap", label: "Ap" },
      { key: "parameter", label: "Parameter" }
    ].concat(CC_DIAMETER_COLS);
  }

  function buildUncoatedPlaceholderRows() {
    var base = {
      iso: "P",
      vdi: "1–2",
      material: "",
      ae: "",
      ap: ""
    };

    var params = ["SFM(vc)", "IPT(fz)", "RPM", "IPM(feed)"];

    return params.map(function (p, idx) {
      var out = {};
      out.iso = idx === 0 ? base.iso : "";
      out.vdi = idx === 0 ? base.vdi : "";
      out.material = idx === 0 ? base.material : "";
      out.ae = idx === 0 ? base.ae : "";
      out.ap = idx === 0 ? base.ap : "";
      out.parameter = p;

      CC_DIAMETER_COLS.forEach(function (c) {
        out[c.key] = "";
      });

      return out;
    });
  }

  function shouldForceUncoatedLayout(data) {
    var t = normText(data && data.title);

    if (t.indexOf("uncoated") !== -1) return true;

    var cols = normalizeColumns(data && data.columns);
    var labels = cols.map(function (c) { return normText(c && c.label); });
    var hasISO = labels.indexOf("iso") !== -1;
    var hasVDI = labels.indexOf("vdi") !== -1;

    if (hasISO && !hasVDI && (t.indexOf("conditions") !== -1 || t.indexOf("cutting") !== -1)) {
      return true;
    }

    return false;
  }

  function forceUncoatedToMatchCoated(data) {
    return {
      title: data && data.title ? data.title : "Uncoated Side-Cutting Conditions",
      columns: buildCcColumns(),
      rows: buildUncoatedPlaceholderRows()
    };
  }

  // ============================================================================
  // PAGINATION
  // ============================================================================

  function createPaginationControls(totalPages, currentPage, onPageChange) {
    var nav = document.createElement("div");
    nav.className = "yg1-pagination";
    nav.setAttribute("role", "navigation");
    nav.setAttribute("aria-label", "Catalog pagination");

    var prevBtn = document.createElement("button");
    prevBtn.type = "button";
    prevBtn.className = "yg1-pagination__btn yg1-pagination__prev";
    prevBtn.textContent = "← Previous";
    prevBtn.disabled = currentPage === 0;
    prevBtn.addEventListener("click", function () {
      if (currentPage > 0) onPageChange(currentPage - 1);
    });
    nav.appendChild(prevBtn);

    var pageNumbers = document.createElement("div");
    pageNumbers.className = "yg1-pagination__numbers";

    for (var i = 0; i < totalPages; i++) {
      (function (pageNum) {
        var pageBtn = document.createElement("button");
        pageBtn.type = "button";
        pageBtn.className = "yg1-pagination__page";
        if (pageNum === currentPage) {
          pageBtn.className += " yg1-pagination__page--active";
          pageBtn.setAttribute("aria-current", "page");
        }
        pageBtn.textContent = String(pageNum + 1);
        pageBtn.addEventListener("click", function () {
          onPageChange(pageNum);
        });
        pageNumbers.appendChild(pageBtn);
      })(i);
    }
    nav.appendChild(pageNumbers);

    var nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "yg1-pagination__btn yg1-pagination__next";
    nextBtn.textContent = "Next →";
    nextBtn.disabled = currentPage === totalPages - 1;
    nextBtn.addEventListener("click", function () {
      if (currentPage < totalPages - 1) onPageChange(currentPage + 1);
    });
    nav.appendChild(nextBtn);

    return nav;
  }

  // ---------------- Interactive table renderer (v2) ----------------
  // - Search box looks through ALL catalogue pages and shows every match together
  // - Column filters: tick a column, type a value -> exact match on that column
  // - Several ticked columns combine (all must match)

  var YG1_FILTER_CSS_ADDED = false;

  function addFilterCss() {
    if (YG1_FILTER_CSS_ADDED) return;
    YG1_FILTER_CSS_ADDED = true;
    var st = document.createElement("style");
    st.textContent = [
      ".yg1-colfilters{display:flex;flex-wrap:wrap;align-items:center;gap:.6rem 1.6rem;margin:1rem 0 .25rem;}",
      ".yg1-colfilter{display:flex;align-items:center;gap:.45rem;font-size:1.1rem;}",
      ".yg1-colfilter label{display:flex;align-items:center;gap:.4rem;cursor:pointer;white-space:nowrap;font-weight:600;color:#222;}",
      ".yg1-colfilter input[type=checkbox]{width:1.1rem;height:1.1rem;margin:0;cursor:pointer;}",
      ".yg1-colfilter input[type=text]{width:7.5rem;padding:.35rem .55rem;font-size:1.05rem;border:2px solid #ccc;border-radius:5px;}",
      ".yg1-colfilter input[type=text]:focus{outline:none;border-color:#0066cc;}",
      ".yg1-colfilters-clear{font-size:1.05rem;font-weight:700;color:#0066cc;background:none;border:none;text-decoration:underline;cursor:pointer;padding:0;}",
      ".yg1-results-note{font-size:1.05rem;font-weight:600;color:#555;margin:.75rem 0 0;}"
    ].join("");
    document.head.appendChild(st);
  }

  var YG1_NUM_RE = /^[-+]?(\d+\.?\d*|\.\d+)$/;

  // Exact match for column filters. "55" = "55.0", ".0394" = "0.0394".
  // Cells with several lines match if any line matches.
  function exactCellMatch(cellVal, wanted) {
    var w = normText(wanted);
    if (!w) return true;
    var parts = String(cellVal == null ? "" : cellVal).split(/\n/);
    for (var i = 0; i < parts.length; i++) {
      var a = normText(parts[i]);
      if (a === w) return true;
      if (YG1_NUM_RE.test(a) && YG1_NUM_RE.test(w) && parseFloat(a) === parseFloat(w)) return true;
    }
    return false;
  }

  function columnSignature(cols) {
    return cols.map(function (c) { return (c && c.key) || ""; }).join("|");
  }

  // Readable filter labels from header_lines, e.g. "Drill Diameter Metric"
  function buildFilterColumns(pages) {
    var out = [];
    var seen = Object.create(null);

    pages.forEach(function (pg) {
      var cols = normalizeColumns(pg && pg.columns);
      var linesOf = function (c) {
        return (c && Array.isArray(c.header_lines))
          ? c.header_lines.map(function (s) { return String(s == null ? "" : s).trim(); })
          : [];
      };

      var groups = Object.create(null);
      cols.forEach(function (c) {
        var top = linesOf(c)[0] || "";
        if (!top) return;
        (groups[top] = groups[top] || []).push(linesOf(c));
      });

      cols.forEach(function (c) {
        var key = c && c.key;
        if (!key || seen[key]) return;
        seen[key] = true;

        var lines = linesOf(c);
        var label = lines[0] || "";
        var sibs = label ? groups[label] : null;

        if (sibs && sibs.length > 1) {
          var depth = 0;
          sibs.forEach(function (l) { if (l.length > depth) depth = l.length; });
          for (var i = 1; i < depth; i++) {
            var vals = sibs.map(function (l) { return l[i] || ""; });
            var allSame = vals.every(function (v) { return v === vals[0]; });
            if (!allSame) {
              if (lines[i]) label += " " + lines[i];
              break;
            }
          }
        }

        if (!label) label = (c && c.label) ? String(c.label) : key;
        out.push({ key: key, label: label });
      });
    });

    return out;
  }

  function buildHeader(cols) {
    var thead = document.createElement("thead");

    var maxHeaderRows = 0;
    cols.forEach(function (c) {
      if (c && Array.isArray(c.header_lines) && c.header_lines.length > maxHeaderRows) {
        maxHeaderRows = c.header_lines.length;
      }
    });

    if (maxHeaderRows > 0) {
      var grid = cols.map(function (c) {
        var lines = (c && Array.isArray(c.header_lines)) ? c.header_lines.slice() : [];
        while (lines.length < maxHeaderRows) lines.push("");
        return lines;
      });

      var covered = [];
      for (var r = 0; r < maxHeaderRows; r++) {
        covered[r] = [];
        for (var ci = 0; ci < cols.length; ci++) covered[r][ci] = false;
      }

      for (var row = 0; row < maxHeaderRows; row++) {
        var tr = document.createElement("tr");
        tr.className = "yg1-header-row-" + (row + 1);

        for (var col = 0; col < cols.length; col++) {
          if (covered[row][col]) continue;

          var cellText = grid[col][row];
          if (cellText === "" && row > 0) continue;

          var rowspan = 1;
          for (var r2 = row + 1; r2 < maxHeaderRows; r2++) {
            if (grid[col][r2] === "") rowspan++;
            else break;
          }

          var colspan = 1;
          for (var c2 = col + 1; c2 < cols.length; c2++) {
            if (grid[c2][row] === cellText && cellText !== "") {
              var otherRowspan = 1;
              for (var r3 = row + 1; r3 < maxHeaderRows; r3++) {
                if (grid[c2][r3] === "") otherRowspan++;
                else break;
              }
              if (otherRowspan === rowspan) colspan++;
              else break;
            } else {
              break;
            }
          }

          for (var sr = row; sr < row + rowspan; sr++) {
            for (var sc = col; sc < col + colspan; sc++) covered[sr][sc] = true;
          }

          var th = document.createElement("th");
          th.textContent = cellText;
          if (rowspan > 1) {
            th.rowSpan = rowspan;
            th.classList.add("yg1-header-rowspan");
          }
          if (colspan > 1) th.colSpan = colspan;
          tr.appendChild(th);
        }

        thead.appendChild(tr);
      }
    } else {
      var trh = document.createElement("tr");
      trh.className = "yg1-header-row-1";
      cols.forEach(function (c) {
        var th2 = document.createElement("th");
        th2.textContent = c && c.label ? c.label : (c && c.key ? c.key : "");
        trh.appendChild(th2);
      });
      thead.appendChild(trh);
    }

    return thead;
  }

  function isSkuColumnDef(c) {
    var key = c && c.key ? c.key : "";
    var type = c && c.type ? c.type : "";
    return (
      type === "variant_sku" ||
      key === "uncoated" ||
      key === "uncoted" ||
      key === "tin" ||
      key === "ticn" ||
      key === "tylon_f" ||
      key === "tylon_e" ||
      key === "edp" ||
      (key && key.toLowerCase().indexOf("edp") !== -1)
    );
  }

  function buildInteractiveTable(cols, rows) {
    var tableWrap = document.createElement("div");
    tableWrap.className = "yg1-interactive-table-wrap";

    var table = document.createElement("table");
    table.className = "yg1-interactive-table";
    table.appendChild(buildHeader(cols));

    var tbody = document.createElement("tbody");
    rows.forEach(function (r) {
      r = r || {};
      var tr = document.createElement("tr");
      cols.forEach(function (c) {
        var td = document.createElement("td");
        var key = c && c.key ? c.key : "";
        var val = r[key];
        if (val == null) val = "";
        if (isSkuColumnDef(c)) maybeLinkSkuCell(td, String(val));
        else td.textContent = String(val);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    tableWrap.appendChild(table);
    return tableWrap;
  }

  function rowTextForSearch(cols, row) {
    var parts = [];
    cols.forEach(function (c) {
      var k = c && c.key ? c.key : "";
      if (!k) return;
      var v = row && row[k];
      if (v == null) return;
      parts.push(String(v));
    });
    return normText(parts.join(" "));
  }

  function renderInteractiveTable(mount, data, seriesLabel, paginationData) {
    addFilterCss();
    mount.innerHTML = "";

    var pd = paginationData || { isPaginated: false, pages: [data], currentPage: 0, totalPages: 1 };

    var pages = (pd.pages || []).map(function (pg) {
      return (pg && shouldForceUncoatedLayout(pg)) ? forceUncoatedToMatchCoated(pg) : pg;
    });

    var state = { query: "", filters: Object.create(null) };

    // ---- top: title + search + count ----
    var top = document.createElement("div");
    top.className = "yg1-interactive-top";

    var title = document.createElement("div");
    title.className = "yg1-interactive-title";
    top.appendChild(title);

    var subtitle = document.createElement("div");
    subtitle.className = "yg1-interactive-subtitle";
    top.appendChild(subtitle);

    var centerControls = document.createElement("div");
    centerControls.className = "yg1-interactive-center";

    var search = document.createElement("input");
    search.type = "search";
    search.className = "yg1-interactive-search";
    search.placeholder = "Search all pages (EDP, diameter, radius, etc.)…";
    centerControls.appendChild(search);

    var count = document.createElement("span");
    count.className = "yg1-interactive-count";
    centerControls.appendChild(count);

    top.appendChild(centerControls);

    // ---- column filters ----
    var filterCols = buildFilterColumns(pages);
    var filterBar = document.createElement("div");
    filterBar.className = "yg1-colfilters";

    var filterInputs = [];

    filterCols.forEach(function (fc) {
      var wrap = document.createElement("div");
      wrap.className = "yg1-colfilter";

      var lab = document.createElement("label");
      var cb = document.createElement("input");
      cb.type = "checkbox";
      lab.appendChild(cb);
      lab.appendChild(document.createTextNode(fc.label));
      wrap.appendChild(lab);

      var inp = document.createElement("input");
      inp.type = "text";
      inp.placeholder = "Exact value";
      inp.setAttribute("aria-label", fc.label + " exact value");
      inp.style.display = "none";
      wrap.appendChild(inp);

      cb.addEventListener("change", function () {
        if (cb.checked) {
          state.filters[fc.key] = inp.value;
          inp.style.display = "";
          inp.focus();
        } else {
          delete state.filters[fc.key];
          inp.value = "";
          inp.style.display = "none";
        }
        refresh();
      });

      inp.addEventListener("input", function () {
        if (cb.checked) state.filters[fc.key] = inp.value;
        scheduleRefresh();
      });

      filterInputs.push({ cb: cb, inp: inp });
      filterBar.appendChild(wrap);
    });

    var clearBtn = document.createElement("button");
    clearBtn.type = "button";
    clearBtn.className = "yg1-colfilters-clear";
    clearBtn.textContent = "Clear search & filters";
    clearBtn.addEventListener("click", function () {
      state.query = "";
      search.value = "";
      state.filters = Object.create(null);
      filterInputs.forEach(function (f) {
        f.cb.checked = false;
        f.inp.value = "";
        f.inp.style.display = "none";
      });
      refresh();
    });
    if (filterCols.length) filterBar.appendChild(clearBtn);

    if (filterCols.length) top.appendChild(filterBar);
    mount.appendChild(top);

    var navTop = document.createElement("div");
    var results = document.createElement("div");
    var navBottom = document.createElement("div");
    mount.appendChild(navTop);
    mount.appendChild(results);
    mount.appendChild(navBottom);

    var timer = null;
    function scheduleRefresh() {
      clearTimeout(timer);
      timer = setTimeout(refresh, 200);
    }

    search.addEventListener("input", function () {
      state.query = search.value;
      scheduleRefresh();
    });

    function activeFilters() {
      var out = [];
      Object.keys(state.filters).forEach(function (k) {
        if (normText(state.filters[k])) out.push({ key: k, value: state.filters[k] });
      });
      return out;
    }

    function goToPage(n) {
      pd.currentPage = n;
      refresh();
    }

    function setTitle(pg) {
      title.textContent = (pg && pg.title)
        ? pg.title
        : ("Interactive Catalog" + (seriesLabel ? " — " + seriesLabel : ""));
      subtitle.textContent = (pg && pg.subtitle) ? pg.subtitle : "";
      subtitle.style.display = subtitle.textContent ? "" : "none";
    }

    function refresh() {
      results.innerHTML = "";
      navTop.innerHTML = "";
      navBottom.innerHTML = "";

      var q = normText(state.query);
      var filters = activeFilters();
      var cur = pages[pd.currentPage] || pages[0];
      setTitle(cur);

      // ---- normal paged view ----
      if (!q && !filters.length) {
        if (pd.isPaginated) {
          navTop.appendChild(createPaginationControls(pd.totalPages, pd.currentPage, goToPage));
          navBottom.appendChild(createPaginationControls(pd.totalPages, pd.currentPage, goToPage));
        }

        var cols = normalizeColumns(cur && cur.columns);
        var rows = normalizeRows(cur && cur.rows);

        if (!cols.length) {
          results.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">No table columns available.</div>';
          count.textContent = "";
          return;
        }

        results.appendChild(buildInteractiveTable(cols, rows));
        count.textContent = rows.length + " rows" +
          (pd.isPaginated ? " (Page " + (pd.currentPage + 1) + " of " + pd.totalPages + ")" : "");
        enhanceCuttingConditionsTables(results);
        return;
      }

      // ---- search / filter view: every page ----
      var groups = [];
      var bySig = Object.create(null);
      var total = 0;
      var totalRows = 0;
      var pagesHit = [];

      pages.forEach(function (pg, idx) {
        var cols = normalizeColumns(pg && pg.columns);
        var rows = normalizeRows(pg && pg.rows);
        totalRows += rows.length;
        if (!cols.length) return;

        var colKeys = Object.create(null);
        cols.forEach(function (c) { if (c && c.key) colKeys[c.key] = true; });

        var hits = rows.filter(function (row) {
          if (q && rowTextForSearch(cols, row).indexOf(q) === -1) return false;
          for (var i = 0; i < filters.length; i++) {
            if (!colKeys[filters[i].key]) return false;
            if (!exactCellMatch(row && row[filters[i].key], filters[i].value)) return false;
          }
          return true;
        });

        if (!hits.length) return;
        pagesHit.push(idx + 1);

        var sig = columnSignature(cols);
        if (!bySig[sig]) {
          bySig[sig] = { cols: cols, rows: [] };
          groups.push(bySig[sig]);
        }
        bySig[sig].rows = bySig[sig].rows.concat(hits);
        total += hits.length;
      });

      count.textContent = total + " of " + totalRows + " rows" +
        (pd.isPaginated ? " (all " + pd.totalPages + " pages)" : "");

      if (!total) {
        results.innerHTML = '<div class="yg1-results-note">No matching items.</div>';
        return;
      }

      if (pd.isPaginated) {
        var note = document.createElement("div");
        note.className = "yg1-results-note";
        note.textContent = "Showing matches from page" + (pagesHit.length > 1 ? "s " : " ") + pagesHit.join(", ");
        results.appendChild(note);
      }

      groups.forEach(function (g) {
        results.appendChild(buildInteractiveTable(g.cols, g.rows));
      });
      enhanceCuttingConditionsTables(results);
    }

    refresh();
  }

  function initInteractiveCatalogFromJson(panelOrRoot) {
    var container = panelOrRoot.querySelector && panelOrRoot.querySelector("#yg1-tab-interactive");
    if (!container) return;

    if (container.getAttribute("data-yg1-rendered") === "true") return;
    container.setAttribute("data-yg1-rendered", "true");

    var mount = container.querySelector(".yg1-interactive-mount") || container;
    var seriesCode = (container.getAttribute("data-series-code") || "").trim();
    var seriesHandle = (container.getAttribute("data-series-handle") || "").trim();
    var seriesLabel = seriesCode || seriesHandle;

    var catalogUrl = (container.getAttribute("data-yg1-catalog-url") || "").trim();

    if (!catalogUrl) {
      mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">Interactive catalog URL missing.</div>';
      return;
    }

    mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">Loading catalog…</div>';

    fetch(catalogUrl, { credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (payload) {
        var pages = (payload && Array.isArray(payload.pages)) ? payload.pages : [];

        if (!pages.length) {
          mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">Interactive catalog not available for this tool series.</div>';
          return;
        }

        var paginationData = {
          isPaginated: pages.length > 1,
          pages: pages,
          currentPage: 0,
          totalPages: pages.length
        };

        renderInteractiveTable(mount, pages[0], seriesLabel, paginationData);
      })
      .catch(function (err) {
        mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">Interactive catalog could not be loaded.</div>';
        console.error("YG1 catalog fetch failed:", err);
      });
  }

  // ---------------------------------------------------------------------------
  // ISO Chart + single-panel CC renderers
  // ---------------------------------------------------------------------------

  function isoKey(sectionData) {
    var key = (sectionData && (sectionData.iso_label || sectionData.iso))
      ? String(sectionData.iso_label || sectionData.iso)
      : "";
    return key.toUpperCase().trim();
  }

  function sumVDI(sectionData) {
    var blocks = Array.isArray(sectionData.blocks) ? sectionData.blocks : [];
    var total = 0;
    blocks.forEach(function (block) {
      total += Array.isArray(block.vdi) ? block.vdi.length : 0;
    });
    return total;
  }

  function ensureArrayLen(arr, length) {
    var out = Array.isArray(arr) ? arr.slice() : [];
    while (out.length < length) out.push("");
    if (out.length > length) out = out.slice(0, length);
    return out;
  }

  function dotHTML(kind) {
    if (!kind) return "";
    var key = String(kind).toLowerCase().trim();
    if (key === "excellent") {
      return '<span class="yg1-iso-dot yg1-iso-dot--excellent" aria-label="Excellent"></span>';
    }
    if (key === "good") {
      return '<span class="yg1-iso-dot yg1-iso-dot--good" aria-label="Good"></span>';
    }
    return "";
  }

  function renderISOChart(mount, data) {
    mount.innerHTML = "";

    if (!data || data.__parse_error) {
      mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">ISO chart JSON could not be parsed.</div>';
      return;
    }

    var sections = Array.isArray(data.sections) ? data.sections : [];
    if (!sections.length) {
      mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">No ISO chart sections available.</div>';
      return;
    }

    var rows = (Array.isArray(data.layout) && data.layout.length)
      ? data.layout
      : [["P", "M"], ["K", "N"], ["S", "H"]];

    function pick(order) {
      var out = [];
      if (!Array.isArray(order)) return out;
      order.forEach(function (key) {
        var want = String(key || "").toUpperCase().trim();
        var found = sections.find(function (s) { return isoKey(s) === want; });
        if (found) out.push(found);
      });
      return out;
    }

    var wrap = document.createElement("div");
    wrap.className = "yg1-iso-wrap";

    var header = document.createElement("div");
    header.className = "yg1-iso-header";

    var title = document.createElement("div");
    title.className = "yg1-iso-title";
    title.textContent = data.title ? String(data.title) : "ISO Chart";
    header.appendChild(title);

    var legend = document.createElement("div");
    legend.className = "yg1-iso-legend";

    var excellentLabel = (data.legend && data.legend.excellent) ? String(data.legend.excellent) : "Excellent";
    var goodLabel = (data.legend && data.legend.good) ? String(data.legend.good) : "Good";

    var le = document.createElement("span");
    le.className = "yg1-iso-legend-item";
    le.innerHTML = '<span class="yg1-iso-dot yg1-iso-dot--excellent"></span><span>' + excellentLabel + "</span>";

    var lg = document.createElement("span");
    lg.className = "yg1-iso-legend-item";
    lg.innerHTML = '<span class="yg1-iso-dot yg1-iso-dot--good"></span><span>' + goodLabel + "</span>";

    legend.appendChild(le);
    legend.appendChild(lg);
    header.appendChild(legend);
    wrap.appendChild(header);

    function buildTable(groupSections) {
      if (!groupSections || !groupSections.length) return null;

      var rowCols = 0;
      groupSections.forEach(function (s) { rowCols += sumVDI(s); });
      if (!rowCols) return null;

      var table = document.createElement("table");
      table.className = "yg1-iso-table";

      var colgroup = document.createElement("colgroup");
      var leftCol = document.createElement("col");
      leftCol.style.width = "var(--yg1-iso-left)";
      colgroup.appendChild(leftCol);

      var pct = 100 / rowCols;
      for (var ci = 0; ci < rowCols; ci++) {
        var col = document.createElement("col");
        col.style.width = pct + "%";
        colgroup.appendChild(col);
      }
      table.appendChild(colgroup);

      var thead = document.createElement("thead");

      var isoRow = document.createElement("tr");
      var isoLabel = document.createElement("th");
      isoLabel.className = "yg1-iso-left";
      isoLabel.textContent = "ISO";
      isoRow.appendChild(isoLabel);

      groupSections.forEach(function (s) {
        var key = isoKey(s);
        var th = document.createElement("th");
        th.className = "yg1-iso-bar yg1-iso-bar--" + key;
        th.colSpan = Math.max(1, sumVDI(s));
        th.textContent = key;
        isoRow.appendChild(th);
      });
      thead.appendChild(isoRow);

      var matRow = document.createElement("tr");
      var matLabel = document.createElement("th");
      matLabel.className = "yg1-iso-left";
      matLabel.textContent = "Material Description";
      matRow.appendChild(matLabel);

      groupSections.forEach(function (s) {
        var key = isoKey(s);
        var blocks = Array.isArray(s.blocks) ? s.blocks : [];
        blocks.forEach(function (block) {
          var vdi = Array.isArray(block.vdi) ? block.vdi : [];
          var th = document.createElement("th");
          th.className = "yg1-iso-mat yg1-iso-mat--" + key;
          th.colSpan = Math.max(1, vdi.length);
          th.textContent = block && block.material ? String(block.material) : "";
          matRow.appendChild(th);
        });
      });
      thead.appendChild(matRow);

      function addDataRow(label, field) {
        var tr = document.createElement("tr");
        var th = document.createElement("th");
        th.className = "yg1-iso-left";
        th.textContent = label;
        tr.appendChild(th);

        groupSections.forEach(function (s) {
          var blocks = Array.isArray(s.blocks) ? s.blocks : [];
          blocks.forEach(function (block) {
            var len = Array.isArray(block.vdi) ? block.vdi.length : 0;
            var values = ensureArrayLen(block && block[field], len);
            for (var vi = 0; vi < len; vi++) {
              var td = document.createElement("td");
              td.textContent = values[vi] == null ? "" : String(values[vi]);
              tr.appendChild(td);
            }
          });
        });
        thead.appendChild(tr);
      }

      addDataRow("VDI 3323", "vdi");
      addDataRow("HRc", "hrc");
      addDataRow("HB", "hb");

      function addRecRow(label, recKey, useLegacy) {
        var tr = document.createElement("tr");
        var th = document.createElement("th");
        th.className = "yg1-iso-left";
        th.textContent = label;
        tr.appendChild(th);

        groupSections.forEach(function (s) {
          var blocks = Array.isArray(s.blocks) ? s.blocks : [];
          blocks.forEach(function (block) {
            var len = Array.isArray(block.vdi) ? block.vdi.length : 0;
            var source = [];

            if (useLegacy) {
              source = Array.isArray(block.recommended) ? block.recommended : [];
            } else if (block && block.recommendations && Array.isArray(block.recommendations[recKey])) {
              source = block.recommendations[recKey];
            }

            var values = ensureArrayLen(source, len);
            for (var vi = 0; vi < len; vi++) {
              var td = document.createElement("td");
              td.innerHTML = dotHTML(values[vi]);
              tr.appendChild(td);
            }
          });
        });
        thead.appendChild(tr);
      }

      var recRows = Array.isArray(data.recommendation_rows) ? data.recommendation_rows : [];

      if (recRows.length > 0) {
        recRows.forEach(function (def) {
          if (!def) return;
          var k = def.key == null ? "" : String(def.key).trim();
          var l = def.label == null ? k : String(def.label);
          if (!k) return;
          addRecRow(l, k, false);
        });
      } else {
        addRecRow("Recommended", "", true);
      }

      table.appendChild(thead);
      return table;
    }

    rows.forEach(function (groupKeys) {
      var table = buildTable(pick(groupKeys));
      if (table) wrap.appendChild(table);
    });

    mount.appendChild(wrap);
  }

  function renderCuttingConditionsTable(mount, data) {
    mount.innerHTML = "";

    if (!data || data.__parse_error) {
      mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">Cutting conditions JSON could not be parsed.</div>';
      return;
    }

    var cols = Array.isArray(data.columns) ? data.columns : [];
    var rows = Array.isArray(data.rows) ? data.rows : [];

    if (!cols.length || !rows.length) {
      mount.innerHTML = '<div style="opacity:.75; padding:.5rem 0;">No cutting conditions rows available.</div>';
      return;
    }

    if (data.title) {
      var heading = document.createElement("div");
      heading.style.fontWeight = "700";
      heading.style.margin = "0 0 .75rem 0";
      heading.textContent = String(data.title);
      mount.appendChild(heading);
    }

    var wrap = document.createElement("div");
    wrap.className = "yg1-interactive-table-wrap";

    var table = document.createElement("table");
    table.className = "yg1-interactive-table";

    var thead = document.createElement("thead");
    var headerRow = document.createElement("tr");
    headerRow.className = "yg1-header-row-1";

    cols.forEach(function (c) {
      var th = document.createElement("th");
      th.textContent = (c && c.label) ? c.label : (c && c.key ? c.key : "");
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    var tbody = document.createElement("tbody");
    rows.forEach(function (row) {
      var tr = document.createElement("tr");
      cols.forEach(function (c) {
        var key = (c && c.key) ? c.key : "";
        var td = document.createElement("td");
        var value = (row && key && row[key] != null) ? row[key] : "";
        td.textContent = String(value);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    wrap.appendChild(table);
    mount.appendChild(wrap);
  }

  function initCcSinglePanels(panelOrRoot) {
    if (!panelOrRoot.querySelectorAll) return;

    var panels = Array.prototype.slice.call(
      panelOrRoot.querySelectorAll("[data-yg1-cc-single]")
    );

    panels.forEach(function (panel) {
      if (panel.getAttribute("data-rendered") === "true") return;

      var mount = panel.querySelector(".yg1-cc-subpanel-mount");
      if (!mount) return;

      var scriptId = (panel.getAttribute("data-json-script-id") || "").trim();
      var template = (panel.getAttribute("data-template") || "").trim();

      var data = getJsonFromScriptId(scriptId);
      if (!data) return;

      if (template === "iso_chart" || (data && Array.isArray(data.sections) && data.sections.length)) {
        renderISOChart(mount, data);
      } else {
        renderCuttingConditionsTable(mount, data);
      }

      panel.setAttribute("data-rendered", "true");
    });
  }

  // ---------------------------------------------------------------------------
  // Cutting Conditions enhancer (ISO strip + rowspans) — CC TABLES ONLY
  // ---------------------------------------------------------------------------

  function looksLikeCuttingConditionsTable(table) {
    if (!table) return false;

    var ths = Array.from(table.querySelectorAll("thead th"));
    if (!ths.length) return false;

    var labels = ths.map(function (th) { return normText(th.textContent); });

    var hasISO = labels.indexOf("iso") !== -1;
    var hasVDI = labels.indexOf("vdi") !== -1;
    var hasParam = labels.indexOf("parameter") !== -1;

    var hasDiameter = labels.some(function (t) {
      return t === "3/32" || t === "1/8" || t === "5/32" || t === "3/16" || t === "1/4";
    });

    return hasISO && hasVDI && hasParam && hasDiameter;
  }

  function indexOfHeader(table, headerText) {
    var ths = Array.from(table.querySelectorAll("thead th"));
    var wanted = normText(headerText);
    for (var i = 0; i < ths.length; i++) {
      if (normText(ths[i].textContent) === wanted) return i;
    }
    return -1;
  }

  function cellAt(row, idx) {
    if (!row) return null;
    var tds = Array.from(row.children || []);
    return (idx >= 0 && idx < tds.length) ? tds[idx] : null;
  }

  function setCellHidden(td) {
    if (!td) return;
    td.style.display = "none";
  }

  function enhanceOneCuttingConditionsTable(table) {
    if (!looksLikeCuttingConditionsTable(table)) return;
    if (table.getAttribute("data-yg1-cc-enhanced") === "true") return;

    table.classList.add("yg1-cc-table");

    var idxISO = indexOfHeader(table, "ISO");
    var idxVDI = indexOfHeader(table, "VDI");
    var idxMaterial = indexOfHeader(table, "Material");
    var idxAe = indexOfHeader(table, "Ae");
    var idxAp = indexOfHeader(table, "Ap");
    var idxParam = indexOfHeader(table, "Parameter");

    var tbodyRows = Array.from(table.querySelectorAll("tbody tr"));
    if (!tbodyRows.length) return;

    for (var r = 0; r < tbodyRows.length; r++) {
      var tr = tbodyRows[r];

      var tdParam = cellAt(tr, idxParam);
      if (!tdParam) continue;

      var paramText = normText(tdParam.textContent);

      if (paramText === "sfm(vc)" || paramText === "sfm") {
        var tdISO = cellAt(tr, idxISO);
        var isoVal = tdISO ? String(tdISO.textContent || "").trim() : "";
        if (!isoVal) continue;

        var span = 4;

        if (tdISO) {
          tdISO.classList.add("yg1-cc-iso-strip");
          tdISO.setAttribute("data-iso", isoVal);
          tdISO.textContent = isoVal;
          tdISO.rowSpan = span;
          tdISO.style.display = "";
        }

        var tdVDI = cellAt(tr, idxVDI);
        var tdMat = cellAt(tr, idxMaterial);
        var tdAe = cellAt(tr, idxAe);
        var tdAp = cellAt(tr, idxAp);

        if (tdVDI) tdVDI.rowSpan = span;
        if (tdMat) tdMat.rowSpan = span;
        if (tdAe) tdAe.rowSpan = span;
        if (tdAp) tdAp.rowSpan = span;

        for (var k = 1; k < span; k++) {
          var tr2 = tbodyRows[r + k];
          if (!tr2) continue;
          setCellHidden(cellAt(tr2, idxISO));
          setCellHidden(cellAt(tr2, idxVDI));
          setCellHidden(cellAt(tr2, idxMaterial));
          setCellHidden(cellAt(tr2, idxAe));
          setCellHidden(cellAt(tr2, idxAp));
        }
      }
    }

    table.setAttribute("data-yg1-cc-enhanced", "true");
  }

  function enhanceCuttingConditionsTables(root) {
    var scope = root || document;
    var tables = Array.from(scope.querySelectorAll("table.yg1-interactive-table"));
    tables.forEach(enhanceOneCuttingConditionsTable);
  }

  function observeForNewTables() {
    var obs = new MutationObserver(function (mutations) {
      var shouldScan = false;
      mutations.forEach(function (m) {
        if (m.addedNodes && m.addedNodes.length) shouldScan = true;
      });
      if (shouldScan) {
        enhanceCuttingConditionsTables(document);
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    if (!isDesktop()) return;

    var roots = findTabsRoots();
    if (!roots.length) return;

    roots.forEach(function (root) {
      wireTabs(root);
      enhanceCuttingConditionsTables(root);

      var activeTab = root.querySelector('.yg1-tabs__tab[role="tab"][aria-selected="true"]');
      if (activeTab) {
        var panelId = activeTab.getAttribute("aria-controls");
        var panel = panelId
          ? (document.getElementById(panelId) || root.querySelector("#" + safeEscape(panelId)))
          : null;
        if (panel) {
          initInteractiveCatalogFromJson(panel);
          initCcSinglePanels(panel);
        }
      }
    });

    observeForNewTables();
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();